// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file StatusDot.tsx
 * @input Uses React
 * @output Exports StatusDot component, StatusDotProps, StatusDotVariant types
 * @position Core implementation; consumed by index.ts
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/StatusDot/StatusDot.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/StatusDot/StatusDot.test.tsx (tests for new/changed behavior)
 * - /packages/core/src/StatusDot/index.ts (exports if types change)
 * - /apps/storybook/stories/StatusDot.stories.tsx (storybook stories)
 * - /packages/cli/assets/templates/blocks/components/StatusDot/ (showcase blocks)
 */
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars } from "../theme/tokens.stylex.js";
import { Tooltip } from "../Tooltip/Tooltip.js";
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Pulse animation keyframes
 */
const pulseKeyframes = "xtut5q7-B";

/**
 * Base styles
 */
const styles = {
  base: {
    k1xSpc: "x1rg5ohu",
    kaIpWk: "x16rqkct",
    kmuXW: "x2lah0s",
    kzqmXN: "x1xc55vz",
    kZKoxP: "xdk7pt",
    $$css: true
  },
  pulsing: {
    kKVMdj: "x1mta51n",
    k44tkh: "x1c74tu6",
    kyAemX: "x4hg4is",
    ko0y90: "xa4qsjk",
    $$css: true
  },
  reducedMotion: {
    kjO2d4: "x1aquc0h",
    $$css: true
  }
};

/**
 * Variant styles mapping to theme color tokens
 */
const variants = {
  success: {
    kWkggS: "xdsz4j9",
    $$css: true
  },
  warning: {
    kWkggS: "x1q8g9m5",
    $$css: true
  },
  error: {
    kWkggS: "x1pjz0fi",
    $$css: true
  },
  accent: {
    kWkggS: "x1ewilqj",
    $$css: true
  },
  neutral: {
    kWkggS: "x1q5y3ey",
    $$css: true
  }
};

/**
 * Status dot variant type derived from StatusDotVariantMap.
 * Extensible via module augmentation of StatusDotVariantMap.
 */

/**
 * A small colored dot indicator for status display (online/offline, severity, etc).
 *
 * Fixed 8px size. Renders as a non-focusable `<span>` with `role="img"` and
 * `aria-label` for accessibility. Styles use Astryx theme tokens via StyleX.
 * Wrap your app in `<Theme>` to apply a theme.
 *
 * @example
 * ```
 * <StatusDot variant="success" label="Online" />
 * <StatusDot variant="error" label="Offline" />
 * <StatusDot variant="success" label="Live" isPulsing />
 * <StatusDot variant="success" label="Online" tooltip="Online" />
 * ```
 */
export function StatusDot({
  variant,
  label,
  isPulsing = false,
  tooltip,
  xstyle,
  className,
  style,
  ref,
  ...props
}) {
  const dot = /*#__PURE__*/_jsx("span", {
    ref: ref,
    role: "img",
    "aria-label": label,
    ...mergeProps(themeProps('statusdot', {
      variant
    }), stylex.props(styles.base, variants[variant], isPulsing && styles.pulsing, isPulsing && styles.reducedMotion, xstyle), className, style),
    ...props
  });
  if (tooltip) {
    return /*#__PURE__*/_jsx(Tooltip, {
      content: tooltip,
      children: dot
    });
  }
  return dot;
}
StatusDot.displayName = 'StatusDot';